import { Button, Group, Stack } from '@mantine/core';
import { IconFileSpreadsheet, IconFileZip } from '@tabler/icons-react';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Content, NoContentPlaceholder, PendingSpinner } from '@/core/components';
import { BooleanFilter, SelectFilter, useBooleanFilter, useFilter } from '@/core/components/Layout/Filters';
import { Pagination } from '@/core/components/Pagination/Pagination';
import { usePagination } from '@/core/components/Pagination/usePagination';

import { useCurrentUser } from '@feature/security';
import { PagedResponse, useAppNotification } from '@feature/utils';
import { FilterBox } from '@feature/utils/components/filters/FilterBox';
import { SearchFilter } from '@feature/utils/components/filters/Search';
import { useSearch, useSortDirection } from '@feature/utils/components/filters/useFilters';

import { UserDocumentCompletenessTable } from './components/UserDocumentCompletenessTable';
import { UserDocumentOrgFilter, useUserDocumentScope } from './components/UserDocumentOrgFilter';
import { UserDocumentSummaryStrip } from './components/UserDocumentSummaryStrip';
import { UserDocumentUserPanel } from './components/UserDocumentUserPanel';
import {
  getUserDocumentSummaryOptions,
  getUserDocumentUsersPagedOptions,
  useUserDocumentArchiveDownload,
  useUserDocumentExportDownload,
} from './model/UserDocumentQuery';
import {
  UserDocumentAdminParams,
  UserDocumentKind,
  UserDocumentsRow,
  userDocumentKindList,
} from './model/UserDocumentTypes';
import { isUserDocumentAdminRole } from './userDocumentRoles';

interface ListingProps {
  params: UserDocumentAdminParams;
  onOpenUser: (row: UserDocumentsRow) => void;
  toggleSortDirection: () => void;
  useTotalElements: (response: PagedResponse<unknown> | undefined | number) => void;
  paginationProps: React.ComponentProps<typeof Pagination>;
}

function UserDocumentAdminListing(props: ListingProps) {
  const { params, onOpenUser, toggleSortDirection, useTotalElements, paginationProps } = props;
  const { t } = useTranslation();
  const { data } = useSuspenseQuery(getUserDocumentUsersPagedOptions(params));

  useTotalElements(data);

  if (data.list.length === 0) {
    return <NoContentPlaceholder title={t('noContent.title')} />;
  }

  return (
    <>
      <UserDocumentCompletenessTable
        rows={data.list}
        sortDirection={params.sortDirection}
        toggleSortDirection={toggleSortDirection}
        onOpenUser={onOpenUser}
      />
      <Pagination {...paginationProps} />
    </>
  );
}

function UserDocumentAdminContent() {
  const { t, i18n } = useTranslation();
  const notification = useAppNotification();

  const scope = useUserDocumentScope();
  const { search, searchProps } = useSearch();
  const { sortDirection, toggleSortDirection } = useSortDirection('DESC');
  const { id: kind, ...kindFilterProps } = useFilter();
  const { item: missingOnly, ...missingOnlyProps } = useBooleanFilter();
  const { pagination, useTotalElements, resetPagination, ...paginationProps } = usePagination({ search });

  const [openedUser, setOpenedUser] = useState<UserDocumentsRow | null>(null);

  const archive = useUserDocumentArchiveDownload();
  const documentExport = useUserDocumentExportDownload();

  const unitScope = { facultyId: scope.facultyId, departmentId: scope.departmentId, disciplineId: scope.disciplineId };
  const filter = { ...unitScope, kind: kind as UserDocumentKind | null, missingOnly };

  // The unit selects write through the url, so they reach no onChange of ours where the other filters reset the page.
  useEffect(() => {
    resetPagination();
  }, [scope.facultyId, scope.departmentId, scope.disciplineId, resetPagination]);

  const { data: summary } = useQuery({
    ...getUserDocumentSummaryOptions(unitScope),
    enabled: scope.isReady,
  });

  const kindOptions = userDocumentKindList.map(value => ({
    value,
    label: t(`userDocuments.kindEnum.${value}`),
  }));

  const onArchive = () => archive.mutate(unitScope, { onError: notification.error });

  const onExport = () =>
    documentExport.mutate({ ...filter, search, lang: i18n.language }, { onError: notification.error });

  return (
    <Stack>
      <FilterBox>
        <Suspense fallback={<PendingSpinner h="80px" />}>
          <UserDocumentOrgFilter />
        </Suspense>
        <Group
          grow
          align="flex-end"
        >
          <SearchFilter
            label={t('label.search')}
            description={t('userDocuments.admin.searchBy')}
            {...searchProps}
          />
          <SelectFilter
            label={t('userDocuments.admin.filters.kind')}
            options={kindOptions}
            isLoading={false}
            isError={false}
            {...kindFilterProps}
            onChange={value => {
              kindFilterProps.onChange(value);
              resetPagination();
            }}
          />
          <BooleanFilter
            label={t('userDocuments.admin.filters.missingOnly')}
            {...missingOnlyProps}
            onChange={value => {
              missingOnlyProps.onChange(value);
              resetPagination();
            }}
          />
        </Group>
      </FilterBox>

      <Content type="full">
        <Group justify="space-between">
          <UserDocumentSummaryStrip summary={summary} />
          <Group gap="xs">
            <Button
              variant="outline"
              leftSection={<IconFileZip size={16} />}
              onClick={onArchive}
              loading={archive.isPending}
              disabled={!scope.isReady}
            >
              {t('userDocuments.admin.downloadArchive')}
            </Button>
            <Button
              variant="outline"
              leftSection={<IconFileSpreadsheet size={16} />}
              onClick={onExport}
              loading={documentExport.isPending}
              disabled={!scope.isReady}
            >
              {t('userDocuments.admin.exportExcel')}
            </Button>
          </Group>
        </Group>
      </Content>

      {!scope.isReady && <NoContentPlaceholder title={t('userDocuments.admin.selectScope')} />}
      {scope.isReady && (
        <Content type="full">
          <Suspense fallback={<PendingSpinner h="300px" />}>
            <UserDocumentAdminListing
              params={{ ...pagination, ...filter, search, sortDirection }}
              onOpenUser={setOpenedUser}
              toggleSortDirection={toggleSortDirection}
              useTotalElements={useTotalElements}
              paginationProps={paginationProps}
            />
          </Suspense>
        </Content>
      )}

      {openedUser != null && (
        <UserDocumentUserPanel
          userId={openedUser.user.id}
          userName={openedUser.user.name ?? `#${openedUser.user.id}`}
          onClose={() => setOpenedUser(null)}
        />
      )}
    </Stack>
  );
}

export function UserDocumentAdminBrowse() {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();

  if (!isUserDocumentAdminRole(currentRole)) {
    return <NoContentPlaceholder title={t('userDocuments.admin.noAccess')} />;
  }

  return <UserDocumentAdminContent />;
}
